/**
 * University Detail Page - Modern Clean Design
 * Beautiful, intuitive layout with excellent visual hierarchy
 */

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@voilajsx/uikit/button';
import {
  Loader2, ArrowLeft, ExternalLink, Users, DollarSign, GraduationCap,
  MapPin, Heart, Home, Briefcase, Cloud, Plane, Coffee,
  TrendingUp, BookOpen, Globe, Award, Shield, Wifi, Bus, ChevronRight,
  Calendar, Clock, Target
} from 'lucide-react';
import { universityAPI } from '../api/university-api';
import { SEO, Header } from '../../../shared/components';
import type { University } from '../types';

// Modern clean color palette with green theme
const colors = {
  primary: '#059669',      // Dark green (Emerald 600)
  primaryLight: '#34D399', // Light green (Emerald 400)
  secondary: '#64748B',    // Slate
  accent: '#F59E0B',       // Amber
  success: '#10B981',      // Emerald
  info: '#14B8A6',         // Teal
  purple: '#14B8A6',       // Teal (replacing purple)
  orange: '#F97316',       // Orange
  rose: '#F43F5E',         // Rose
  background: '#F8FAFC',   // Very light slate
  card: '#FFFFFF',         // White
  border: '#E2E8F0',       // Light slate
  text: '#0F172A',         // Dark slate
  textMuted: '#64748B',    // Muted slate
  textLight: '#94A3B8'     // Light slate
};

const UniversityDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [university, setUniversity] = useState<University | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState('overview');

  // Refs for scroll sections
  const overviewRef = useRef<HTMLDivElement>(null);
  const programsRef = useRef<HTMLDivElement>(null);
  const costsRef = useRef<HTMLDivElement>(null);
  const lifeRef = useRef<HTMLDivElement>(null);
  const internationalRef = useRef<HTMLDivElement>(null);

  const backUrl = location.state?.from || '/university/browse';

  const navItems = [
    { id: 'overview', label: 'Overview', icon: Home, ref: overviewRef },
    { id: 'programs', label: 'Programs', icon: GraduationCap, ref: programsRef },
    { id: 'costs', label: 'Costs & Living', icon: DollarSign, ref: costsRef },
    { id: 'life', label: 'Campus Life', icon: Coffee, ref: lifeRef },
    { id: 'international', label: 'International', icon: Globe, ref: internationalRef },
  ];

  useEffect(() => {
    const loadUniversity = async () => {
      if (!slug) {
        setError('No university specified');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const data = await universityAPI.getUniversityById(slug as any);
        setUniversity(data);
      } catch (err: any) {
        console.error('Failed to load university:', err);
        setError(err.message || 'Failed to load university');
      } finally {
        setIsLoading(false);
      }
    };

    loadUniversity();
  }, [slug]);

  // Scroll spy to update active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => ({
        id: item.id,
        element: item.ref.current
      }));

      const scrollPosition = window.scrollY + 120;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.element && section.element.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [university]);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.background }}>
        <div className="flex items-center gap-3">
          <Loader2 className="h-7 w-7 animate-spin" style={{ color: colors.primary }} />
          <span style={{ color: colors.text, fontSize: '16px', fontWeight: '500' }}>Loading university details...</span>
        </div>
      </div>
    );
  }

  if (error || !university) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.background }}>
        <div className="text-center">
          <p className="mb-4" style={{ color: colors.rose, fontSize: '16px' }}>{error || 'University not found'}</p>
          <Button onClick={() => navigate(backUrl)} variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Browse
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${university.name} - Complete University Guide`}
        description={`Discover ${university.name} - Programs, costs, student life, climate, and everything you need to know`}
      />

      <Header />

      <div style={{ minHeight: '100vh', background: colors.background }}>
        {/* Modern Hero Banner */}
        <div style={{
          background: '#1E293B',
          position: 'relative',
          overflow: 'hidden',
          paddingBottom: '48px'
        }}>
          {/* Background Image with Overlay */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(https://images.unsplash.com/photo-1562774053-701939374585?w=1400&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.2
          }}></div>

          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.9) 0%, rgba(4, 120, 87, 0.95) 100%)'
          }}></div>

          <div className="container mx-auto px-6 py-6 relative z-10">
            {/* Back Button */}
            <button
              onClick={() => navigate(backUrl)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '32px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'translateX(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>

            {/* Hero Content */}
            <div className="max-w-5xl">
              {/* University Name */}
              <h1 style={{
                fontSize: '3.75rem',
                fontWeight: '800',
                color: 'white',
                marginBottom: '24px',
                lineHeight: '1',
                letterSpacing: '-0.02em'
              }}>
                {university.name}
              </h1>

              {/* Location & Badges */}
              <div className="flex flex-wrap items-center gap-3 mb-8">
                <div style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  padding: '12px 20px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  height: '48px'
                }}>
                  <span style={{ fontSize: '20px', lineHeight: '1' }}>{university.country.flag_emoji}</span>
                  <span style={{ fontWeight: '600', fontSize: '16px', color: 'white' }}>{university.country.name}</span>
                </div>

                {university.city && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    padding: '12px 20px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    height: '48px'
                  }}>
                    <MapPin className="h-5 w-5" style={{ color: 'white' }} />
                    <span style={{ fontWeight: '600', fontSize: '16px', color: 'white' }}>{university.city}</span>
                  </div>
                )}

                {university.qs_world_ranking && (
                  <div style={{
                    background: colors.accent,
                    padding: '12px 20px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                    height: '48px'
                  }}>
                    <Award className="h-5 w-5" style={{ color: 'white' }} />
                    <span style={{ fontWeight: '700', fontSize: '16px', color: 'white' }}>
                      #{university.qs_world_ranking} World Rank
                    </span>
                  </div>
                )}
              </div>

              {/* Key Stats - Modern Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {university.total_students && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(10px)',
                    padding: '20px',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '6px', fontWeight: '500' }}>
                      Total Students
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: 'white', lineHeight: '1' }}>
                      {(university.total_students / 1000).toFixed(1)}k
                    </div>
                  </div>
                )}

                {university.acceptance_rate && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(10px)',
                    padding: '20px',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '6px', fontWeight: '500' }}>
                      Acceptance Rate
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: 'white', lineHeight: '1' }}>
                      {university.acceptance_rate}%
                    </div>
                  </div>
                )}

                {university.international_students && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(10px)',
                    padding: '20px',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '6px', fontWeight: '500' }}>
                      International
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: 'white', lineHeight: '1' }}>
                      {(university.international_students / 1000).toFixed(1)}k
                    </div>
                  </div>
                )}

                {university.programs && university.programs.length > 0 && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(10px)',
                    padding: '20px',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '6px', fontWeight: '500' }}>
                      Programs
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: 'white', lineHeight: '1' }}>
                      {university.programs.length}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons - Floating */}
            <div className="flex gap-3 mt-8">
              <button style={{
                background: 'white',
                color: colors.primary,
                padding: '16px 32px',
                borderRadius: '12px',
                fontWeight: '600',
                fontSize: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
              }}>
                <Heart className="h-5 w-5" />
                <span>Save University</span>
              </button>

              {university.website_url && (
                <a
                  href={university.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    padding: '16px 32px',
                    borderRadius: '12px',
                    fontWeight: '600',
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    textDecoration: 'none',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <ExternalLink className="h-5 w-5" />
                  <span>Visit Website</span>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Main Content with Sidebar */}
        <div className="container mx-auto px-6 py-12">
          <div className="grid grid-cols-12 gap-10">
            {/* Modern Sticky Sidebar */}
            <div className="col-span-12 lg:col-span-3">
              <div style={{
                position: 'sticky',
                top: '100px'
              }}>
                <div style={{
                  background: colors.card,
                  borderRadius: '20px',
                  padding: '24px',
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                  border: `1px solid ${colors.border}`
                }}>
                  <h3 style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '20px'
                  }}>
                    Quick Navigation
                  </h3>

                  <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {navItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeSection === item.id;

                      return (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.ref)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 16px',
                            borderRadius: '12px',
                            border: 'none',
                            background: isActive ? colors.primary : 'transparent',
                            color: isActive ? 'white' : colors.textMuted,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            fontWeight: isActive ? '600' : '500',
                            fontSize: '14px',
                            textAlign: 'left',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = '#F1F5F9';
                              e.currentTarget.style.color = colors.text;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = colors.textMuted;
                            }
                          }}
                        >
                          {isActive && (
                            <div style={{
                              position: 'absolute',
                              left: 0,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: '3px',
                              height: '60%',
                              background: 'white',
                              borderRadius: '0 2px 2px 0'
                            }}></div>
                          )}
                          <Icon className="h-5 w-5" style={{ flexShrink: 0 }} />
                          <span style={{ flex: 1 }}>{item.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="col-span-12 lg:col-span-9">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>

                {/* Overview Section */}
                <section ref={overviewRef} id="overview">
                  <div style={{ marginBottom: '32px' }}>
                    <h2 style={{
                      fontSize: '32px',
                      fontWeight: '800',
                      color: colors.text,
                      marginBottom: '8px',
                      letterSpacing: '-0.01em'
                    }}>
                      Overview
                    </h2>
                    <p style={{ fontSize: '16px', color: colors.textMuted }}>
                      Key information and statistics about {university.name}
                    </p>
                  </div>

                  {/* Modern Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div style={{
                      background: colors.card,
                      borderRadius: '20px',
                      padding: '32px',
                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                      border: `1px solid ${colors.border}`,
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '-20px',
                        right: '-20px',
                        width: '100px',
                        height: '100px',
                        background: colors.info,
                        opacity: 0.05,
                        borderRadius: '50%'
                      }}></div>
                      <div style={{ position: 'relative' }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '14px',
                          background: `${colors.info}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '20px'
                        }}>
                          <Users className="h-7 w-7" style={{ color: colors.info }} />
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: colors.textMuted, marginBottom: '8px' }}>
                          Total Students
                        </div>
                        <div style={{ fontSize: '36px', fontWeight: '800', color: colors.text, letterSpacing: '-0.02em' }}>
                          {university.total_students?.toLocaleString() || 'N/A'}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      background: colors.card,
                      borderRadius: '20px',
                      padding: '32px',
                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                      border: `1px solid ${colors.border}`,
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '-20px',
                        right: '-20px',
                        width: '100px',
                        height: '100px',
                        background: colors.success,
                        opacity: 0.05,
                        borderRadius: '50%'
                      }}></div>
                      <div style={{ position: 'relative' }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '14px',
                          background: `${colors.success}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '20px'
                        }}>
                          <TrendingUp className="h-7 w-7" style={{ color: colors.success }} />
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: colors.textMuted, marginBottom: '8px' }}>
                          Acceptance Rate
                        </div>
                        <div style={{ fontSize: '36px', fontWeight: '800', color: colors.text, letterSpacing: '-0.02em' }}>
                          {university.acceptance_rate || 'N/A'}%
                        </div>
                      </div>
                    </div>

                    <div style={{
                      background: colors.card,
                      borderRadius: '20px',
                      padding: '32px',
                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                      border: `1px solid ${colors.border}`,
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '-20px',
                        right: '-20px',
                        width: '100px',
                        height: '100px',
                        background: colors.purple,
                        opacity: 0.05,
                        borderRadius: '50%'
                      }}></div>
                      <div style={{ position: 'relative' }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '14px',
                          background: `${colors.purple}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '20px'
                        }}>
                          <Globe className="h-7 w-7" style={{ color: colors.purple }} />
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: colors.textMuted, marginBottom: '8px' }}>
                          International Students
                        </div>
                        <div style={{ fontSize: '36px', fontWeight: '800', color: colors.text, letterSpacing: '-0.02em' }}>
                          {university.international_students?.toLocaleString() || 'N/A'}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      background: colors.card,
                      borderRadius: '20px',
                      padding: '32px',
                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                      border: `1px solid ${colors.border}`,
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '-20px',
                        right: '-20px',
                        width: '100px',
                        height: '100px',
                        background: colors.accent,
                        opacity: 0.05,
                        borderRadius: '50%'
                      }}></div>
                      <div style={{ position: 'relative' }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '14px',
                          background: `${colors.accent}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '20px'
                        }}>
                          <Award className="h-7 w-7" style={{ color: colors.accent }} />
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: colors.textMuted, marginBottom: '8px' }}>
                          QS World Ranking
                        </div>
                        <div style={{ fontSize: '36px', fontWeight: '800', color: colors.text, letterSpacing: '-0.02em' }}>
                          #{university.qs_world_ranking || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location Card */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '20px',
                    padding: '36px',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                    border: `1px solid ${colors.border}`
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '28px'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: `${colors.primary}10`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <MapPin className="h-6 w-6" style={{ color: colors.primary }} />
                      </div>
                      <h3 style={{
                        fontSize: '22px',
                        fontWeight: '700',
                        color: colors.text
                      }}>
                        Location & Campus
                      </h3>
                    </div>

                    <div className="grid md:grid-cols-2 gap-10">
                      <div>
                        <h4 style={{
                          fontSize: '13px',
                          fontWeight: '700',
                          color: colors.textMuted,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '16px'
                        }}>
                          Details
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                          {[
                            { label: 'City', value: university.city || 'N/A' },
                            { label: 'Region', value: university.state_province || 'N/A' },
                            { label: 'Country', value: `${university.country.flag_emoji} ${university.country.name}` },
                            { label: 'Type', value: university.university_type || 'N/A' }
                          ].map((item) => (
                            <div key={item.label} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              paddingBottom: '14px',
                              borderBottom: `1px solid ${colors.border}`
                            }}>
                              <span style={{ color: colors.textLight, fontSize: '15px', fontWeight: '500' }}>{item.label}</span>
                              <span style={{ fontWeight: '600', color: colors.text, fontSize: '15px' }}>{item.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 style={{
                          fontSize: '13px',
                          fontWeight: '700',
                          color: colors.textMuted,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '16px'
                        }}>
                          Campus Features
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {[
                            { icon: Wifi, title: 'Modern Infrastructure', desc: 'State-of-the-art facilities' },
                            { icon: Bus, title: 'Easy Commute', desc: 'Well-connected transport' },
                            { icon: Shield, title: 'Safe Environment', desc: '24/7 campus security' }
                          ].map((feature, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'start', gap: '14px' }}>
                              <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '10px',
                                background: `${colors.primary}10`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}>
                                <feature.icon className="h-5 w-5" style={{ color: colors.primary }} />
                              </div>
                              <div>
                                <div style={{ fontWeight: '600', color: colors.text, fontSize: '15px', marginBottom: '2px' }}>
                                  {feature.title}
                                </div>
                                <div style={{ fontSize: '14px', color: colors.textMuted }}>
                                  {feature.desc}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Programs Section */}
                <section ref={programsRef} id="programs">
                  <div style={{ marginBottom: '32px' }}>
                    <h2 style={{
                      fontSize: '32px',
                      fontWeight: '800',
                      color: colors.text,
                      marginBottom: '8px',
                      letterSpacing: '-0.01em'
                    }}>
                      Available Programs
                    </h2>
                    <p style={{ fontSize: '16px', color: colors.textMuted }}>
                      {university.programs?.length || 0} programs currently offered
                    </p>
                  </div>

                  {university.programs && university.programs.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {university.programs.map((program: any) => (
                        <div
                          key={program.id}
                          style={{
                            background: colors.card,
                            borderRadius: '20px',
                            padding: '28px',
                            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                            border: `1px solid ${colors.border}`,
                            transition: 'all 0.3s',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(15, 23, 42, 0.12)';
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.borderColor = colors.primary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(15, 23, 42, 0.08)';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.borderColor = colors.border;
                          }}
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                            <div style={{ flex: 1 }}>
                              <h3 style={{
                                fontSize: '20px',
                                fontWeight: '700',
                                color: colors.text,
                                marginBottom: '14px',
                                letterSpacing: '-0.01em'
                              }}>
                                {program.name}
                              </h3>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                {program.field && (
                                  <span style={{
                                    background: `${colors.purple}15`,
                                    color: colors.purple,
                                    padding: '8px 16px',
                                    borderRadius: '10px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}>
                                    <BookOpen className="h-4 w-4" />
                                    {program.field}
                                  </span>
                                )}
                                {program.degree_type && (
                                  <span style={{
                                    background: colors.background,
                                    color: colors.textMuted,
                                    padding: '8px 16px',
                                    borderRadius: '10px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                  }}>
                                    {program.degree_type}
                                  </span>
                                )}
                                {program.duration_years && (
                                  <span style={{
                                    background: colors.background,
                                    color: colors.textMuted,
                                    padding: '8px 16px',
                                    borderRadius: '10px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}>
                                    <Clock className="h-4 w-4" />
                                    {program.duration_years} years
                                  </span>
                                )}
                              </div>
                            </div>

                            {program.tuition_usd && (
                              <div style={{
                                background: colors.primary,
                                padding: '24px 32px',
                                borderRadius: '16px',
                                textAlign: 'center',
                                minWidth: '180px'
                              }}>
                                <div style={{
                                  fontSize: '12px',
                                  color: 'rgba(255, 255, 255, 0.85)',
                                  marginBottom: '6px',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  Annual Tuition
                                </div>
                                <div style={{
                                  fontSize: '40px',
                                  fontWeight: '900',
                                  color: 'white',
                                  lineHeight: '1',
                                  letterSpacing: '-0.02em'
                                }}>
                                  ${(program.tuition_usd / 1000).toFixed(0)}k
                                </div>
                                <div style={{
                                  fontSize: '12px',
                                  color: 'rgba(255, 255, 255, 0.8)',
                                  marginTop: '6px',
                                  fontWeight: '500'
                                }}>
                                  USD per year
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      background: colors.card,
                      borderRadius: '20px',
                      padding: '80px 40px',
                      textAlign: 'center',
                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                      border: `1px solid ${colors.border}`
                    }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: colors.background,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px'
                      }}>
                        <GraduationCap className="h-10 w-10" style={{ color: colors.textLight }} />
                      </div>
                      <p style={{ color: colors.textMuted, fontSize: '18px', fontWeight: '500' }}>
                        No program information available
                      </p>
                      <p style={{ fontSize: '15px', color: colors.textLight, marginTop: '8px' }}>
                        Contact the university for program details
                      </p>
                    </div>
                  )}
                </section>

                {/* Costs Section */}
                <section ref={costsRef} id="costs">
                  <div style={{ marginBottom: '32px' }}>
                    <h2 style={{
                      fontSize: '32px',
                      fontWeight: '800',
                      color: colors.text,
                      marginBottom: '8px',
                      letterSpacing: '-0.01em'
                    }}>
                      Costs & Living Expenses
                    </h2>
                    <p style={{ fontSize: '16px', color: colors.textMuted }}>
                      Tuition fees and estimated living costs
                    </p>
                  </div>

                  {/* Tuition Card */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '20px',
                    padding: '36px',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                    border: `1px solid ${colors.border}`,
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '28px'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: `${colors.success}10`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <DollarSign className="h-6 w-6" style={{ color: colors.success }} />
                      </div>
                      <h3 style={{
                        fontSize: '22px',
                        fontWeight: '700',
                        color: colors.text
                      }}>
                        Tuition Fees
                      </h3>
                    </div>

                    <div className="grid md:grid-cols-2 gap-10">
                      <div style={{
                        background: colors.success,
                        padding: '36px',
                        borderRadius: '16px',
                        color: 'white',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: '13px',
                          opacity: 0.9,
                          marginBottom: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Annual Tuition Range
                        </div>
                        <div style={{
                          fontSize: '48px',
                          fontWeight: '900',
                          lineHeight: '1',
                          marginBottom: '8px',
                          letterSpacing: '-0.02em'
                        }}>
                          ${university.tuition_usd_min ? (university.tuition_usd_min / 1000).toFixed(0) : '?'}k
                          {university.tuition_usd_max && ` - ${(university.tuition_usd_max / 1000).toFixed(0)}k`}
                        </div>
                        <div style={{ fontSize: '13px', opacity: 0.85, fontWeight: '500' }}>USD per year</div>
                      </div>

                      <div>
                        <h4 style={{
                          fontSize: '13px',
                          fontWeight: '700',
                          color: colors.textMuted,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '20px'
                        }}>
                          What's Included
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                          {[
                            { text: 'Academic instruction & coursework', included: true },
                            { text: 'Library & laboratory access', included: true },
                            { text: 'Student services & support', included: true },
                            { text: 'Housing & accommodation', included: false }
                          ].map((item, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: item.included ? colors.success : colors.accent,
                                flexShrink: 0
                              }}></div>
                              <span style={{
                                color: colors.textMuted,
                                fontSize: '15px',
                                fontWeight: '500'
                              }}>
                                {item.text}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Living Costs Card */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '20px',
                    padding: '36px',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                    border: `1px solid ${colors.border}`
                  }}>
                    <h3 style={{
                      fontSize: '22px',
                      fontWeight: '700',
                      color: colors.text,
                      marginBottom: '8px'
                    }}>
                      Estimated Living Costs
                    </h3>
                    <p style={{ fontSize: '15px', color: colors.textMuted, marginBottom: '28px' }}>
                      Monthly expenses in {university.city || university.country.name}
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                      {[
                        { icon: Home, label: 'Housing (Shared)', cost: '$400 - $800' },
                        { icon: Coffee, label: 'Food & Groceries', cost: '$300 - $500' },
                        { icon: Bus, label: 'Transportation', cost: '$50 - $150' },
                        { icon: Wifi, label: 'Internet & Phone', cost: '$50 - $100' }
                      ].map((item, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingBottom: '18px',
                          borderBottom: idx < 3 ? `1px solid ${colors.border}` : 'none'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '10px',
                              background: colors.background,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <item.icon className="h-5 w-5" style={{ color: colors.textMuted }} />
                            </div>
                            <span style={{ fontSize: '16px', color: colors.text, fontWeight: '500' }}>{item.label}</span>
                          </div>
                          <span style={{ fontSize: '17px', fontWeight: '700', color: colors.text }}>{item.cost}</span>
                        </div>
                      ))}

                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '24px',
                        background: colors.success,
                        borderRadius: '16px',
                        marginTop: '12px'
                      }}>
                        <span style={{ fontSize: '18px', fontWeight: '700', color: 'white' }}>
                          Total Monthly (Estimated)
                        </span>
                        <span style={{ fontSize: '32px', fontWeight: '900', color: 'white', letterSpacing: '-0.02em' }}>
                          $800 - $1,550
                        </span>
                      </div>
                    </div>

                    <p style={{ fontSize: '13px', color: colors.textLight, marginTop: '20px', fontStyle: 'italic' }}>
                      * Costs are approximate and may vary based on lifestyle and location
                    </p>
                  </div>
                </section>

                {/* Campus Life Section */}
                <section ref={lifeRef} id="life">
                  <div style={{ marginBottom: '32px' }}>
                    <h2 style={{
                      fontSize: '32px',
                      fontWeight: '800',
                      color: colors.text,
                      marginBottom: '8px',
                      letterSpacing: '-0.01em'
                    }}>
                      Campus Life & Climate
                    </h2>
                    <p style={{ fontSize: '16px', color: colors.textMuted }}>
                      Student life, facilities, and weather information
                    </p>
                  </div>

                  {/* Climate Card */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '20px',
                    padding: '36px',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                    border: `1px solid ${colors.border}`,
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '28px'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: `${colors.info}10`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Cloud className="h-6 w-6" style={{ color: colors.info }} />
                      </div>
                      <h3 style={{
                        fontSize: '22px',
                        fontWeight: '700',
                        color: colors.text
                      }}>
                        Climate & Weather
                      </h3>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                      {[
                        {
                          bg: colors.info,
                          label: 'Climate Type',
                          value: university.country.name === 'Canada' ? 'Continental' : 'Temperate'
                        },
                        {
                          bg: colors.orange,
                          label: 'Summer',
                          value: '20-30°C',
                          sub: 'Warm & Pleasant'
                        },
                        {
                          bg: '#06b6d4',
                          label: 'Winter',
                          value: '-10-5°C',
                          sub: 'Cold & Snowy'
                        }
                      ].map((item, idx) => (
                        <div key={idx} style={{
                          background: item.bg,
                          padding: '28px',
                          borderRadius: '16px',
                          color: 'white',
                          textAlign: 'center'
                        }}>
                          <div style={{
                            fontSize: '13px',
                            opacity: 0.9,
                            marginBottom: '10px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {item.label}
                          </div>
                          <div style={{ fontSize: '28px', fontWeight: '800', lineHeight: '1', marginBottom: '6px' }}>
                            {item.value}
                          </div>
                          {item.sub && (
                            <div style={{ fontSize: '13px', opacity: 0.85, fontWeight: '500' }}>
                              {item.sub}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <p style={{ fontSize: '15px', color: colors.textMuted, marginTop: '24px', textAlign: 'center' }}>
                      Four distinct seasons. Pack warm clothing for winter months!
                    </p>
                  </div>

                  {/* Facilities Card */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '20px',
                    padding: '36px',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                    border: `1px solid ${colors.border}`
                  }}>
                    <h3 style={{
                      fontSize: '22px',
                      fontWeight: '700',
                      color: colors.text,
                      marginBottom: '28px'
                    }}>
                      Campus Facilities & Amenities
                    </h3>

                    <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                      {[
                        'Libraries & Study Spaces',
                        'Dining Halls & Cafeterias',
                        'Sports & Recreation Centers',
                        'On-Campus Housing',
                        'Student Clubs & Societies',
                        'Transportation Services',
                        'Health & Wellness Services',
                        'Campus Security 24/7',
                        'Career Counseling',
                        'High-Speed WiFi'
                      ].map((facility, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: colors.primary,
                            flexShrink: 0
                          }}></div>
                          <span style={{ fontSize: '16px', color: colors.text, fontWeight: '500' }}>
                            {facility}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                {/* International Students Section */}
                <section ref={internationalRef} id="international">
                  <div style={{ marginBottom: '32px' }}>
                    <h2 style={{
                      fontSize: '32px',
                      fontWeight: '800',
                      color: colors.text,
                      marginBottom: '8px',
                      letterSpacing: '-0.01em'
                    }}>
                      International Students
                    </h2>
                    <p style={{ fontSize: '16px', color: colors.textMuted }}>
                      Support services and opportunities for international students
                    </p>
                  </div>

                  {/* Support Services Card */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '20px',
                    padding: '36px',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                    border: `1px solid ${colors.border}`,
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '28px'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: `${colors.purple}10`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Plane className="h-6 w-6" style={{ color: colors.purple }} />
                      </div>
                      <h3 style={{
                        fontSize: '22px',
                        fontWeight: '700',
                        color: colors.text
                      }}>
                        International Student Support
                      </h3>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                      {[
                        {
                          icon: Globe,
                          bg: `${colors.purple}10`,
                          color: colors.purple,
                          title: 'International Office',
                          desc: 'Dedicated support team'
                        },
                        {
                          icon: Briefcase,
                          bg: `${colors.success}10`,
                          color: colors.success,
                          title: 'Work Permit Support',
                          desc: 'Part-time work authorization'
                        },
                        {
                          icon: Users,
                          bg: `${colors.info}10`,
                          color: colors.info,
                          title: 'Student Community',
                          desc: 'Active student associations'
                        }
                      ].map((service, idx) => (
                        <div key={idx} style={{
                          background: service.bg,
                          padding: '24px',
                          borderRadius: '16px'
                        }}>
                          <div style={{ marginBottom: '16px' }}>
                            <service.icon className="h-7 w-7" style={{ color: service.color }} />
                          </div>
                          <div style={{
                            fontWeight: '700',
                            color: colors.text,
                            fontSize: '16px',
                            marginBottom: '6px'
                          }}>
                            {service.title}
                          </div>
                          <div style={{ fontSize: '14px', color: colors.textMuted }}>
                            {service.desc}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* For Indian Students */}
                    <div style={{
                      marginTop: '32px',
                      padding: '28px',
                      background: '#FFF7ED',
                      borderRadius: '16px',
                      border: '1px solid #FED7AA'
                    }}>
                      <h4 style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: colors.text,
                        marginBottom: '20px'
                      }}>
                        For Indian Students
                      </h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        {[
                          'Indian food options available',
                          'Active Indian Student Association',
                          'Diwali & Holi celebrations',
                          'Cricket & kabaddi clubs'
                        ].map((feature, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: colors.orange
                            }}></div>
                            <span style={{ fontSize: '15px', color: colors.text, fontWeight: '500' }}>
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Part-time Work Card */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '20px',
                    padding: '36px',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                    border: `1px solid ${colors.border}`
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '28px'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: `${colors.success}10`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Briefcase className="h-6 w-6" style={{ color: colors.success }} />
                      </div>
                      <h3 style={{
                        fontSize: '22px',
                        fontWeight: '700',
                        color: colors.text
                      }}>
                        Part-Time Work Opportunities
                      </h3>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 mb-8">
                      {[
                        { label: 'Work Hours Allowed', value: '20 hrs/week', sub: 'During semester', bg: colors.success },
                        { label: 'Average Hourly Rate', value: '$15-25', sub: 'CAD per hour', bg: colors.info },
                        { label: 'Monthly Earnings', value: '$1,200-2,000', sub: 'Estimated', bg: colors.purple }
                      ].map((stat, idx) => (
                        <div key={idx} style={{
                          background: stat.bg,
                          padding: '24px',
                          borderRadius: '16px',
                          color: 'white',
                          textAlign: 'center'
                        }}>
                          <div style={{
                            fontSize: '12px',
                            opacity: 0.9,
                            marginBottom: '8px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {stat.label}
                          </div>
                          <div style={{ fontSize: '28px', fontWeight: '800', lineHeight: '1', marginBottom: '6px' }}>
                            {stat.value}
                          </div>
                          <div style={{ fontSize: '12px', opacity: 0.85, fontWeight: '500' }}>
                            {stat.sub}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <h4 style={{
                        fontSize: '13px',
                        fontWeight: '700',
                        color: colors.textMuted,
                        marginBottom: '20px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Popular Job Options
                      </h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        {[
                          'Campus Library Assistant',
                          'Teaching Assistant (TA)',
                          'Retail & Hospitality',
                          'Food Service',
                          'Research Assistant',
                          'Tutoring'
                        ].map((job, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '16px',
                            background: colors.background,
                            borderRadius: '12px',
                            border: `1px solid ${colors.border}`
                          }}>
                            <Briefcase className="h-5 w-5" style={{ color: colors.primary }} />
                            <span style={{ fontSize: '15px', color: colors.text, fontWeight: '500' }}>
                              {job}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>

              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UniversityDetailPage;
